import { useState } from 'react'
import {
  Activity,
  CheckCircle2,
  Calendar,
  Package,
  UtensilsCrossed,
  UserPlus,
  Plus,
  ChevronDown,
  ChevronRight,
  TrendingUp } from '@/ui/icons'
import type { LucideIcon } from '@/ui/icons'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { useBackendNotificationsRealtime } from '@/shared/hooks/useNotificationsRealtime'
import { cn } from '@/shared/utils'

interface ActivityItem {
  action: string
  time: string
  icon: LucideIcon
  color: string
  actionUrl?: string
}

interface SidebarRecentActivityProps {
  tripId: string
}

const SIDEBAR_ACTIVITY_LIMIT = 10

function normalizeActionUrl(actionUrl: string | undefined): string | undefined {
  if (!actionUrl) return actionUrl
  const mealsMatch = actionUrl.match(/^\/trips\/([^/]+)\/meals$/)
  if (mealsMatch) return `/trips/${mealsMatch[1]}?tab=meals`
  const gearMatch = actionUrl.match(/^\/trips\/([^/]+)\/gear$/)
  if (gearMatch) return `/trips/${gearMatch[1]}?tab=gear`
  const membersMatch = actionUrl.match(/^\/trips\/([^/]+)\/members$/)
  if (membersMatch) return `/trips/${membersMatch[1]}?tab=crew`
  return actionUrl
}

function getActivityFromNotification(
  notification: { type?: string; message?: string; title?: string; createdAt?: string; actionUrl?: string },
  typeMap: Record<string, { icon: LucideIcon; color: string }>
): ActivityItem | null {
  if (!notification.createdAt) return null
  const createdAt = new Date(notification.createdAt)
  if (isNaN(createdAt.getTime())) return null
  let formattedTime = 'Just now'
  try {
    formattedTime = formatDistanceToNow(createdAt, { addSuffix: true })
  } catch {
    formattedTime = 'Recently'
  }
  const mapped = typeMap[notification.type ?? '']
  const normalizedActionUrl = normalizeActionUrl(notification.actionUrl)
  if (!mapped) {
    return {
      action: notification.message || notification.title || 'Activity',
      time: formattedTime,
      icon: CheckCircle2,
      color: 'text-earth-600 dark:text-earth-400',
      actionUrl: normalizedActionUrl
    }
  }
  return {
    action: notification.message || notification.title || 'Activity',
    time: formattedTime,
    icon: mapped.icon,
    color: mapped.color,
    actionUrl: normalizedActionUrl
  }
}

const ACTIVITY_TYPE_MAP: Record<string, { icon: LucideIcon; color: string }> = {
  trip_update: { icon: CheckCircle2, color: 'text-green-600' },
  member_joined: { icon: Plus, color: 'text-adventure-600' },
  trip_reminder: { icon: Calendar, color: 'text-earth-600' },
  gear_assigned: { icon: Package, color: 'text-adventure-600' },
  meal_assigned: { icon: UtensilsCrossed, color: 'text-earth-600' },
  guest_access_request: { icon: UserPlus, color: 'text-adventure-600' }
}

export function SidebarRecentActivity({ tripId }: SidebarRecentActivityProps) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(true)
  const { notifications, loading } = useBackendNotificationsRealtime({
    limit: SIDEBAR_ACTIVITY_LIMIT,
    tripId
  })

  const activities = notifications
    .filter((n: { type?: string }) => n.type !== 'photo_uploaded')
    .map((n: { type?: string; message?: string; title?: string; createdAt?: string; actionUrl?: string }) =>
      getActivityFromNotification(n, ACTIVITY_TYPE_MAP)
    )
    .filter((item): item is ActivityItem => item !== null)

  const count = activities.length

  return (
    <div className="flex min-h-0 flex-1 flex-col border-t border-earth-200 dark:border-earth-700">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className={cn(
          'flex w-full items-center gap-2 px-2 py-3 text-left text-xs font-medium',
          'text-earth-800 dark:text-foreground hover:bg-earth-100 dark:hover:bg-earth-200 rounded-md transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-2'
        )}
        aria-expanded={expanded}
        aria-label={`Recent activity, ${count} items, ${expanded ? 'collapse' : 'expand'}`}
      >
        <TrendingUp className="h-3.5 w-3.5 text-earth-500 dark:text-earth-400 flex-shrink-0" aria-hidden />
        <span className="flex-1 min-w-0">Recent Activity</span>
        {count > 0 && (
          <span className="text-earth-600 dark:text-earth-400 flex-shrink-0" aria-hidden>
            {count}
          </span>
        )}
        <span className="flex-shrink-0 text-earth-500 dark:text-earth-400" aria-hidden>
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </span>
      </button>

      {expanded && (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pb-3 space-y-1">
          {loading ? (
            <p className="text-xs text-earth-500 dark:text-earth-400 py-4 text-center">Loading activity...</p>
          ) : activities.length === 0 ? (
            <div className="py-6 flex flex-col items-center justify-center text-center">
              <Activity className="w-8 h-8 text-earth-300 dark:text-earth-600 mb-2" aria-hidden />
              <p className="text-xs font-medium text-earth-800 dark:text-foreground">No Recent Activity</p>
              <p className="text-xs text-earth-500 dark:text-earth-400 mt-0.5">
                Activity from this trip will appear here
              </p>
            </div>
          ) : (
            <ul className="space-y-1" role="list">
              {activities.map((activity, idx) => {
                const IconComponent = activity.icon
                const isClickable = !!activity.actionUrl
                return (
                  <li key={`${idx}-${activity.time}`}>
                    <div
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onKeyDown={
                        isClickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                navigate(activity.actionUrl!)
                              }
                            }
                          : undefined
                      }
                      className={cn(
                        'flex gap-2 p-2 rounded-md transition-colors min-w-0',
                        isClickable && 'cursor-pointer hover:bg-earth-100 dark:hover:bg-earth-200'
                      )}
                      onClick={() => {
                        if (activity.actionUrl) navigate(activity.actionUrl)
                      }}
                    >
                      <div
                        className={cn(
                          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-earth-100 dark:bg-earth-800',
                          activity.color
                        )}
                      >
                        <IconComponent className="w-3.5 h-3.5" aria-hidden />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-earth-900 dark:text-foreground truncate">
                          {activity.action}
                        </p>
                        <p className="text-xs text-earth-500 dark:text-earth-400">{activity.time}</p>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
